/**
 * Hook for fetching and managing member communication responses
 * Supports filtering by enrollment, campaign, referral, or member
 */

import { useState, useEffect, useCallback } from 'react';
import { getWeaveinApiUrl } from '@/src/config';

export interface CommunicationResponse {
  id: number;
  member_id: number;
  cbo_id: number;
  context_type: 'enrollment' | 'campaign' | 'referral' | 'workflow' | 'manual';
  enrollment_id: number | null;
  campaign_id: number | null;
  referral_id: number | null;
  response_type: string;
  response_channel: string;
  response_content: string | null;
  response_action: string | null;
  response_sentiment: string | null;
  response_timestamp: string;
  response_delay_seconds: number | null;
  is_read: boolean;
  read_at: string | null;
  follow_up_required: boolean;
  follow_up_notes: string | null;
  member_name: string | null;
  member_phone: string | null;
  member_email: string | null;
}

export interface ResponseStats {
  total: number;
  unread_count: number;
}

export interface UseCommunicationResponsesOptions {
  memberId?: number;
  enrollmentId?: number;
  campaignId?: number;
  referralId?: number;
  contextType?: string;
  isRead?: boolean;
  followUpRequired?: boolean;
  autoFetch?: boolean;
}

export interface UseCommunicationResponsesReturn {
  responses: CommunicationResponse[];
  loading: boolean;
  error: string | null;
  total: number;
  unreadCount: number;
  refetch: () => Promise<void>;
  markAsRead: (responseId: number) => Promise<void>;
  updateFollowUp: (responseId: number, required: boolean, notes?: string) => Promise<void>;
}

export default function useCommunicationResponses(
  options: UseCommunicationResponsesOptions = {}
): UseCommunicationResponsesReturn {
  const [responses, setResponses] = useState<CommunicationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const {
    memberId,
    enrollmentId,
    campaignId,
    referralId,
    contextType,
    isRead,
    followUpRequired,
    autoFetch = true
  } = options;

  const fetchResponses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      // Build query params
      const params = new URLSearchParams();
      if (memberId) params.append('member_id', memberId.toString());
      if (enrollmentId) params.append('enrollment_id', enrollmentId.toString());
      if (campaignId) params.append('campaign_id', campaignId.toString());
      if (referralId) params.append('referral_id', referralId.toString());
      if (contextType) params.append('context_type', contextType);
      if (isRead !== undefined) params.append('is_read', isRead.toString());
      if (followUpRequired !== undefined) params.append('follow_up_required', followUpRequired.toString());
      params.append('page_size', '100');

      const response = await fetch(
        getWeaveinApiUrl(`/api/workflows/member-responses?${params.toString()}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || '1'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch responses: ${response.status}`);
      }

      const data = await response.json();
      setResponses(data.responses || []);
      setTotal(data.total || 0);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch responses';
      setError(message);
      console.error('Error fetching communication responses:', err);
    } finally {
      setLoading(false);
    }
  }, [memberId, enrollmentId, campaignId, referralId, contextType, isRead, followUpRequired]);

  const markAsRead = useCallback(async (responseId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl(`/api/workflows/member-responses/${responseId}/read`),
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || '1',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to mark as read: ${response.status}`);
      }

      // Update local state
      setResponses(prev => prev.map(r =>
        r.id === responseId ? { ...r, is_read: true, read_at: new Date().toISOString() } : r
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking response as read:', err);
      throw err;
    }
  }, []);

  const updateFollowUp = useCallback(async (responseId: number, required: boolean, notes?: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const params = new URLSearchParams();
      params.append('follow_up_required', required.toString());
      if (notes) params.append('follow_up_notes', notes);

      const response = await fetch(
        getWeaveinApiUrl(`/api/workflows/member-responses/${responseId}/follow-up?${params.toString()}`),
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || '1',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update follow-up: ${response.status}`);
      }

      // Update local state
      setResponses(prev => prev.map(r =>
        r.id === responseId ? { ...r, follow_up_required: required, follow_up_notes: notes || null } : r
      ));
    } catch (err) {
      console.error('Error updating follow-up:', err);
      throw err;
    }
  }, []);

  useEffect(() => {
    if (autoFetch && (memberId || enrollmentId || campaignId || referralId)) {
      fetchResponses();
    }
  }, [autoFetch, memberId, enrollmentId, campaignId, referralId, fetchResponses]);

  return {
    responses,
    loading,
    error,
    total,
    unreadCount,
    refetch: fetchResponses,
    markAsRead,
    updateFollowUp
  };
}
